import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Typography as MuiTypography,
  Grid as MuiGrid,
  MenuItem as MuiMenuItem,
  FormLabel as MuiFormLabel,
  TextField as MuiTextField,
  Switch as MuiSwitch,
} from "@mui/material";
import {
  DatePicker as MuiDatePicker,
  LocalizationProvider as MuiLocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDateFns as MuiAdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { format, isPast, isValid, parseISO } from "date-fns";
import { useFormikContext } from "formik";
import { createEditStudyFormStyles } from "app/components/study/edit/CreateEditStudyFormStyles";
import {
  defaultFUDateFrom,
  defaultDateTo,
  RecruitmentStatusOptions,
} from "app/shared/constants";
import WarningDialog from "app/shared/UI/Dialog/WarningDialog";
import { switchStyles } from "app/shared/UI/sharedStyles";

const CreateEditStudyMain = ({ setIsOptimizationEnabled }) => {
  const { values, setFieldValue, errors, handleBlur, touched } =
    useFormikContext();

  const { t } = useTranslation();
  const [openASConfirmation, setOpenASConfirmation] = useState(false);

  const [followUpDateError, setFollowUpDateError] = useState(false);

  const handleFollowUpDateValidation = (date) => {
    const followUpDate = values?.followUpDate;

    const isFollowUpDateValid =
      followUpDate instanceof Date && !isNaN(followUpDate.getTime());

    // Validation for updating a study
    return format(new Date(date), "MM/dd/yyyy") !==
      format(new Date(), "MM/dd/yyyy") &&
      (isFollowUpDateValid ? followUpDate.getTime() !== date.getTime() : true)
      ? !isPast(new Date(date))
        ? setOpenASConfirmation(false)
        : setOpenASConfirmation(true)
      : true;
  };
  const closeCancelASConfirmation = () => {
    setOpenASConfirmation(false);
  };

  return (
    <>
      <WarningDialog
        open={openASConfirmation}
        onClose={closeCancelASConfirmation}
        title={t("formValidation.followUpDateTitle")}
        primaryMessage={t("formValidation.followUpDate")}
        secondaryMessage={t("formValidation.secondaryFollowUpDate")}
      />
      <MuiGrid container justifyContent="space-between" spacing={2}>
        <MuiGrid size={6}>
          <MuiTextField
            name="nickName"
            fullWidth
            label={t(`formLabel.studyNickname`)}
            variant="outlined"
            onChange={(e) => {
              setFieldValue("nickname", e.target.value);
            }}
            size="small"
            value={values.nickname}
          />
        </MuiGrid>
        <MuiGrid size={6}>
          <MuiTextField
            name="title"
            fullWidth
            label={t(`formLabel.studyTitle`)}
            value={values.title}
            variant="outlined"
            onBlur={handleBlur}
            onChange={(e) => {
              setFieldValue("title", e.target.value);
            }}
            size="small"
            error={touched.title && Boolean(errors.title)}
            helperText={
              touched.title && errors.title && t("formValidation.requiredField")
            }
          />
        </MuiGrid>
        <MuiGrid size={6}>
          <MuiTextField
            name="oncoreStatus"
            fullWidth
            label={t(`formLabel.oncoreProtocolStatus`)}
            variant="outlined"
            value={values.protocolStatus || ""}
            size="small"
            disabled
          />
        </MuiGrid>
        <MuiGrid size={6}>
          <MuiTextField
            name="oncoreProtocolNumber"
            fullWidth
            label={t(`formLabel.oncoreProtocolNumber`)}
            variant="outlined"
            value={values.protocolNumber || ""}
            size="small"
            disabled
          />
        </MuiGrid>

        <MuiGrid size={3}>
          <MuiTextField
            name="riskCount"
            fullWidth
            label={t(`formLabel.riskCount`)}
            variant="outlined"
            value={values.riskCount || 0}
            size="small"
            disabled
          />
        </MuiGrid>
        <MuiGrid size={3}>
          <MuiTextField
            name="recruited"
            fullWidth
            label={t(`formLabel.recruited`)}
            variant="outlined"
            value={
              values.recruited +
              " (" +
              values.subjectSummary?.enrolled +
              "/" +
              values.protocolTargetAccrual +
              ")"
            }
            size="small"
            disabled
          />
        </MuiGrid>
        <MuiGrid size={3}>
          <MuiTypography component="div">
            <MuiFormLabel component="legend" sx={{ marginBottom: "5px" }}>
              {t("formLabel.suspended")}
            </MuiFormLabel>
            <MuiGrid
              id="suspendedSwitch"
              component="label"
              container
              alignItems="center"
              spacing={1}
            >
              <MuiGrid>{t("common.noLabel")}</MuiGrid>
              <MuiGrid>
                <MuiSwitch
                  sx={switchStyles}
                  name="suspended"
                  checked={values.suspended}
                  value={values.suspended}
                  onChange={(e) => {
                    setFieldValue("suspended", e.target.checked);
                  }}
                />
              </MuiGrid>
              <MuiGrid>{t("common.yesLabel")}</MuiGrid>
            </MuiGrid>
          </MuiTypography>
        </MuiGrid>
        <MuiGrid size={3}>
          <MuiTextField
            select
            fullWidth
            name="honestBrokerRecruitmentStatus"
            label={t(`formLabel.recruitmentStatus`)}
            variant="outlined"
            size="small"
            value={values.honestBrokerRecruitmentStatus}
            onBlur={handleBlur}
            onChange={(e) => {
              setFieldValue("honestBrokerRecruitmentStatus", e.target.value);
            }}
            defaultValue={RecruitmentStatusOptions[0]}
            error={
              touched.honestBrokerRecruitmentStatus &&
              errors.honestBrokerRecruitmentStatus
            }
            helperText={
              touched.honestBrokerRecruitmentStatus &&
              errors.honestBrokerRecruitmentStatus &&
              t("formValidation.uninitiatedError")
            }
          >
            {RecruitmentStatusOptions &&
              RecruitmentStatusOptions.map((item) => (
                <MuiMenuItem key={item} value={item}>
                  {t("recruitmentStatus." + item)}
                </MuiMenuItem>
              ))}
          </MuiTextField>
        </MuiGrid>

        <MuiGrid size={6}>
          <MuiLocalizationProvider dateAdapter={MuiAdapterDateFns}>
            <MuiDatePicker
              name="followUpDate"
              fullWidth
              variant="inline"
              inputVariant="outlined"
              margin="dense"
              format="MM/dd/yyyy"
              label={t(`formLabel.followUpDate`)}
              minDate={defaultFUDateFrom}
              maxDate={defaultDateTo}
              value={
                typeof values.followUpDate === "string"
                  ? parseISO(values.followUpDate)
                  : values.followUpDate || null
              }
              onChange={(date, { validationError }) => {
                if (isValid(date)) {
                  handleFollowUpDateValidation(new Date(date));
                }
                setFieldValue(
                  "followUpDate",
                  isValid(date) ? format(new Date(date), "yyyy-MM-dd") : date,
                );

                if (validationError === null && date === null) {
                  setFollowUpDateError(t("formValidation.requiredField"));
                } else if (validationError === null) {
                  setFollowUpDateError(null);
                }
              }}
              onError={(error) => {
                if (error === "maxDate") {
                  setFollowUpDateError(t("formValidation.invalidDateFormat"));
                } else if (error === "minDate") {
                  setFollowUpDateError(
                    t("formValidation.minDateExceededError", {
                      minDateExceeded: format(defaultFUDateFrom, "MM/dd/yyyy"),
                    }),
                  );
                } else if (error === "invalidDate") {
                  setFollowUpDateError(t("formValidation.invalidDateFormat"));
                } else if (!error) {
                  setFollowUpDateError(null);
                } else {
                  setFollowUpDateError(null);
                }
              }}
              rules={{ required: true }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  size: "small",
                  InputLabelProps: { shrink: true },
                  error: Boolean(followUpDateError),
                  helperText: followUpDateError,
                  required: true,
                },
              }}
              sx={{ width: "100%" }}
            />
          </MuiLocalizationProvider>
        </MuiGrid>
        <MuiGrid size={6}>
          <MuiLocalizationProvider dateAdapter={MuiAdapterDateFns}>
            <MuiDatePicker
              disableToolbar
              autoOk
              fullWidth
              variant="inline"
              inputVariant="outlined"
              margin="dense"
              format="MM/dd/yyyy"
              label={t(`formLabel.irbExpirationDate`)}
              value={
                values.protocolIrbExpirationDate
                  ? parseISO(values.protocolIrbExpirationDate)
                  : null
              }
              disabled
              slotProps={{
                textField: {
                  variant: "outlined",
                  size: "small",
                  InputLabelProps: { shrink: true },
                },
              }}
              sx={{ width: "100%" }}
            />
          </MuiLocalizationProvider>
        </MuiGrid>
        <MuiGrid size={6} sx={createEditStudyFormStyles.customGridMargin}>
          <MuiTextField
            name="note"
            fullWidth
            label={t(`formLabel.note`)}
            variant="outlined"
            multiline={true}
            size="small"
            value={values.note}
            onChange={(e) => {
              setFieldValue("note", e.target.value);
            }}
          />
        </MuiGrid>
        {values && !values.protocolSummaryAccrual ? (
          <MuiGrid size={6}>
            <MuiTypography component="div">
              <MuiFormLabel component="legend" sx={{ marginBottom: "5px" }}>
                {t("targetAccrualsTable.subpopulation_optimization_label")}
              </MuiFormLabel>
              <MuiGrid
                id="optimizationSwitch"
                component="label"
                container
                alignItems="center"
                spacing={1}
              >
                <MuiGrid>{t("common.noLabel")}</MuiGrid>
                <MuiGrid>
                  <MuiSwitch
                    sx={switchStyles}
                    name="isOptimizationEnabled"
                    checked={values.targetAccruals?.isOptimizationEnabled}
                    value={
                      !values.protocolSummaryAccrual &&
                      !!values.targetAccruals?.isOptimizationEnabled
                    }
                    onChange={(e) => {
                      setFieldValue(
                        "targetAccruals.isOptimizationEnabled",
                        e.target.checked,
                      );

                      setIsOptimizationEnabled(e.target.checked);
                    }}
                    disabled={values.protocolSummaryAccrual}
                  />
                </MuiGrid>
                <MuiGrid>{t("common.yesLabel")}</MuiGrid>
              </MuiGrid>
            </MuiTypography>
          </MuiGrid>
        ) : (
          ""
        )}
      </MuiGrid>
    </>
  );
};

export default CreateEditStudyMain;
